import React, {
    createContext,
    FC,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    AccessTokenResponse,
    AuthProviderProps,
    NiamService,
    UseAuth,
    UseProvideAuthProps,
    UserInfoResponse,
    UserPermission,
} from "./NiamAuthTypes";
import { generatePKCE } from "./authUtil";
import {
    fetchAuthUser,
    fetchAuthWebAccessToken,
    fetchRefreshedAccessToken,
} from "./NiamAuthClient";
import { AuthAction, AuthState } from "./AuthState";
import * as jose from "jose";
import config from "../../config/config";

const LOGIN_REDIRECT_URL_SESSION_KEY = "loginRedirectUrl";
const LOGIN_RETURN_URL_SESSION_KEY = "loginReturnUrl";
const LAST_RELOGIN_SESSION_KEY = "lastRelogin";
const AUTH_CALLBACK_PARAMS = ["code", "iss", "client_id", "state"];

const defaultAuthContext = {
    authenticated: false,
    user: null,
    accessToken: null,
    idToken: null,
    error: null,
    resetLogin: () => {},
    login: () => Promise.resolve(),
    relogin: () => Promise.resolve(false),
    logout: () => Promise.resolve(),
    hasPermission: (permission: string) => false,
};

const AuthContext = createContext<UseAuth>(defaultAuthContext);

export const AuthProvider: FC<AuthProviderProps> = ({
    children,
    authorizeUrl,
    deAuthorizeUrl,
    tokenUrl,
    userInfoUrl,
    clientId,
    scopes,
    redirectUrl,
    service,
    permissions,
}) => {
    const auth = useProvideAuth({
        authorizeUrl,
        deAuthorizeUrl,
        tokenUrl,
        userInfoUrl,
        clientId,
        redirectUrl,
        scopes,
        service,
        permissions,
    });
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

function createDisplayNameFromUserInfo(
    userInfoResponse: UserInfoResponse
): string {
    if (userInfoResponse.given_name && userInfoResponse.family_name) {
        return userInfoResponse.given_name + " " + userInfoResponse.family_name;
    }
    if (userInfoResponse.given_name) return userInfoResponse.given_name;
    if (userInfoResponse.family_name) return userInfoResponse.family_name;
    if (userInfoResponse.email) return userInfoResponse.email;
    return "Unknown";
}

function getAuthorizationUrl(
    service: undefined | NiamService,
    authorizeUrl: string,
    tokenUrl: string
): string {
    return service == NiamService.AADLogin
        ? tokenUrl.substring(0, tokenUrl.lastIndexOf("/") + 1) + "/authorize"
        : authorizeUrl;
}

const useProvideAuth = ({
    authorizeUrl,
    deAuthorizeUrl,
    clientId,
    scopes = [],
    redirectUrl,
    tokenUrl,
    service,
    userInfoUrl,
    permissions = [],
}: UseProvideAuthProps): UseAuth => {
    const [authState, setAuthState] = useState<AuthState>(
        AuthState.init(service || null)
    );
    const codeVerifier = sessionStorage.getItem("codeVerifier");
    const { code } = useQueryParams();
    const location = useLocation();
    const navigate = useNavigate();
    const minutesBeforeExpirationThreshold = 5;
    const refreshRetrySeconds = 30;
    const reloginLoopGuardSeconds = 60;
    const loginRedirectStarted = useRef(false);

    //console.log("useProvideAuth state=" + authState.authAction)

    const verifyPermissions = useCallback(
        (userPermissions: UserPermission[]) => {
            const mappedUserPermissions = userPermissions.map(
                (userPermission) => userPermission.name
            );
            const hasAllPermissions = permissions.every((permission) =>
                mappedUserPermissions.includes(permission)
            );
            if (!hasAllPermissions) {
                resetLogin("FORBIDDEN_ACCESS");
            }
        },
        [permissions]
    );

    // Initiate state from session storage
    useEffect(() => {
        const storedAccessToken = sessionStorage.getItem("accessToken");
        const storedIdToken = sessionStorage.getItem("idToken");
        const refreshToken = sessionStorage.getItem("refreshToken");
        const expiresIn = Number(sessionStorage.getItem("expiresIn"));
        if (
            (storedIdToken && isTokenExpired(storedIdToken)) ||
            (storedAccessToken && isTokenExpired(storedAccessToken))
        ) {
            console.log("useProvideAuth loaded tokens have expired REMOVING");
            sessionStorage.removeItem("accessToken");
            sessionStorage.removeItem("idToken");
            sessionStorage.removeItem("refreshToken");
            sessionStorage.removeItem("expiresIn");
            return;
        }
        if (storedAccessToken && storedIdToken && !code) {
            console.log("useProvideAuth loaded tokens from session storage");
            setAuthState((prevAuthState) =>
                prevAuthState.updateTokens(
                    storedAccessToken,
                    storedIdToken,
                    expiresIn,
                    refreshToken
                )
            );
        }
    }, []);

    // challenge callback from NIAM with code to use in actual login
    useEffect(() => {
        if (
            !authState.code ||
            authState.authAction !== AuthAction.AUTHORIZING ||
            !codeVerifier
        ) {
            return;
        }
        const fetchAccessToken = async (
            code: string,
            codeVerifier: string,
            retry: boolean
        ) => {
            try {
                const accessTokenResponse = await fetchAuthWebAccessToken(
                    tokenUrl,
                    code,
                    clientId,
                    codeVerifier,
                    sessionStorage.getItem(LOGIN_REDIRECT_URL_SESSION_KEY) ||
                        redirectUrl
                );
                //console.log("useProvideAuth.fetchAccessToken accessTokenResponse",accessTokenResponse)
                if (!accessTokenResponse) {
                    console.log(
                        "useProvideAuth.fetchAccessToken FAILED accessTokenResponse EMPTY!"
                    );
                    setAuthState((prevAuthState) =>
                        prevAuthState.updateError("Unknown error")
                    );
                    return;
                }
                updateToken(accessTokenResponse);
            } catch (e: any) {
                if (retry) {
                    console.error(
                        "useProvideAuth RETRY in 500ms. Failed to retrieve access token because ",
                        e
                    );
                    setTimeout(
                        () => fetchAccessToken(code, codeVerifier, false),
                        2000
                    );
                    return;
                }
                console.error(
                    "useProvideAuth Failed to retrieve access token because ",
                    e
                );
                setAuthState((prevAuthState) =>
                    prevAuthState.updateError(e.message)
                );
            }
        };
        fetchAccessToken(authState.code, codeVerifier, true);
    }, [authState.code, authState.authAction]);

    const updateToken = (accessTokenResponse: AccessTokenResponse) => {
        sessionStorage.setItem("accessToken", accessTokenResponse.access_token);
        sessionStorage.setItem("idToken", accessTokenResponse.id_token);
        sessionStorage.setItem(
            "expiresIn",
            String(accessTokenResponse.expires_in)
        );
        if (accessTokenResponse.refresh_token) {
            sessionStorage.setItem(
                "refreshToken",
                accessTokenResponse.refresh_token
            );
            console.log(
                "Refresh token received. Will update token automatically within 60 min."
            );
        } else
            console.log(
                "No Refresh token received. No automatic updates will be possible."
            );
        sessionStorage.removeItem("codeVerifier");
        sessionStorage.removeItem(LOGIN_REDIRECT_URL_SESSION_KEY);
        restoreLoginReturnUrl(navigate);
        setAuthState((prevAuthState) =>
            prevAuthState.updateTokens(
                accessTokenResponse.access_token,
                accessTokenResponse.id_token,
                accessTokenResponse.expires_in,
                accessTokenResponse.refresh_token
            )
        );
    };

    useEffect(() => {
        if (
            !code ||
            authState.authAction !== AuthAction.UNAUTHORIZED ||
            !codeVerifier
        ) {
            return;
        }
        setAuthState((prevAuthState) =>
            prevAuthState.initiateAuthorization(code)
        );
    }, [code, authState.authAction]);

    // on new tokens, set timer for renewal
    useEffect(() => {
        const accessToken = authState.accessToken;
        const refreshToken = authState.refreshToken;
        if (!accessToken || !refreshToken) {
            return;
        }
        const refreshTime = millisecondsUntilRefresh(
            accessToken,
            minutesBeforeExpirationThreshold
        );
        if (refreshTime === null) {
            return;
        }

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        const refreshAccessToken = () => {
            console.log("Token about to expire. Renewing");
            fetchRefreshedAccessToken(tokenUrl, refreshToken, clientId)
                .then((accessTokenResponse) => {
                    if (cancelled) {
                        return;
                    }
                    updateToken({
                        ...accessTokenResponse,
                        refresh_token:
                            accessTokenResponse.refresh_token || refreshToken,
                    });
                })
                .catch((e) => {
                    if (cancelled) {
                        return;
                    }
                    if (isTokenExpired(accessToken)) {
                        console.error(
                            "Failed to refresh token, access token has expired",
                            e
                        );
                        return;
                    }
                    console.error(
                        `Failed to refresh token, retrying in ${refreshRetrySeconds}s`,
                        e
                    );
                    timer = setTimeout(
                        refreshAccessToken,
                        refreshRetrySeconds * 1000
                    );
                });
        };

        console.log(
            `Token will be refreshed at ${new Date(Date.now() + refreshTime)}`
        );
        timer = setTimeout(refreshAccessToken, refreshTime);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [authState.accessToken, authState.refreshToken]);

    // Fetch user and permissions, when authorized
    useEffect(() => {
        if (
            authState.authAction !== AuthAction.AUTHORIZED ||
            authState.accessToken === null
        ) {
            return;
        }

        try {
            const decodedAccessToken = jose.decodeJwt(authState.accessToken);
            const { auth_level } = decodedAccessToken;
            const isProd = config.ENVIRONMENT.toUpperCase() === "PROD";
            const validAuthLevels = isProd ? [1, 2] : [0, 1, 2];

            if (
                typeof auth_level !== "number" ||
                !validAuthLevels.includes(auth_level)
            ) {
                console.error(
                    `Invalid auth_level (${auth_level}) for ${config.ENVIRONMENT} environment. Denying access.`
                );
                resetLogin("FORBIDDEN_ACCESS");
                return;
            }
        } catch (err) {
            console.error("Failed to decode token or no auth_level found", err);
            resetLogin("FORBIDDEN_ACCESS");
            return;
        }

        const fetchUser = async (accessToken: string) => {
            try {
                const userInfoResponse = await fetchAuthUser(
                    userInfoUrl,
                    accessToken
                );
                if (!userInfoResponse) {
                    console.log("useProvideAuth.login fetchUser FAILED");
                    return;
                }
                const userPermissions: UserPermission[] =
                    userInfoResponse.permissions.map((permission) =>
                        permission.name != undefined
                            ? permission
                            : { name: permission }
                    );

                setAuthState((prevAuthState) =>
                    prevAuthState.updateUser({
                        sub: userInfoResponse.sub,
                        firstName: userInfoResponse.given_name,
                        lastName: userInfoResponse.family_name,
                        displayName:
                            createDisplayNameFromUserInfo(userInfoResponse),
                        email: userInfoResponse.email,
                        permissions: userPermissions,
                    })
                );
                verifyPermissions(userPermissions);
            } catch (e: any) {
                console.error(
                    "useProvideAuth Failed to retrieve permissions because ",
                    e
                );
                console.log("useProvideAuth userInfoUrl " + userInfoUrl);
                //console.log("useProvideAuth accessToken "+accessToken)
                setAuthState((prevAuthState) =>
                    prevAuthState.updateError(e.message)
                );
            }
        };

        fetchUser(authState.accessToken);
    }, [authState.authAction]);

    const resetLogin = (error?: string) => {
        sessionStorage.removeItem("idToken");
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("expiresIn");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("codeVerifier");
        sessionStorage.removeItem(LOGIN_REDIRECT_URL_SESSION_KEY);
        sessionStorage.removeItem(LOGIN_RETURN_URL_SESSION_KEY);
        setAuthState((prevAuthState) => authState.reset());
    };

    const login = async (niamService?: NiamService) => {
        if (authState.authAction !== AuthAction.UNAUTHORIZED) {
            console.log("Cannot login! Authorization in progress!", authState);
            return;
        }
        await redirectToLogin(niamService);
    };

    const relogin = async (): Promise<boolean> => {
        if (loginRedirectStarted.current) {
            return true;
        }
        const lastRelogin = Number(
            sessionStorage.getItem(LAST_RELOGIN_SESSION_KEY)
        );
        resetLogin();
        if (Date.now() - lastRelogin < reloginLoopGuardSeconds * 1000) {
            console.error(
                "Access was rejected again right after a new login. Not redirecting to login again."
            );
            return false;
        }
        sessionStorage.setItem(LAST_RELOGIN_SESSION_KEY, String(Date.now()));
        await redirectToLogin();
        return true;
    };

    const redirectToLogin = async (niamService?: NiamService) => {
        if (loginRedirectStarted.current) {
            return;
        }
        if (!niamService) {
            if (authState.niamService == null)
                throw new Error("Unable to login! Missing niamService");
            niamService = authState.niamService;
        }

        sessionStorage.setItem(
            LOGIN_RETURN_URL_SESSION_KEY,
            `${location.pathname}${location.search}${location.hash}`
        );
        sessionStorage.setItem(LOGIN_REDIRECT_URL_SESSION_KEY, redirectUrl);

        loginRedirectStarted.current = true;
        try {
            await niamCodeChallenge(
                getAuthorizationUrl(niamService, authorizeUrl, tokenUrl),
                clientId,
                redirectUrl,
                niamService,
                scopes.join(" "),
                config.ENVIRONMENT
            );
        } catch (e) {
            loginRedirectStarted.current = false;
            throw e;
        }
    };

    const logout = async () => {
        if (!authState.accessToken || !authState.idToken) {
            console.log("missing token to logout");
            return;
        }
        await niamDeAuthorize(
            deAuthorizeUrl,
            authState.idToken,
            authState.accessToken
        );
        resetLogin();
    };

    const hasPermission = (permission: string): boolean => {
        return (
            !!permission &&
            !!authState.user?.permissions.some((userPermission) =>
                permission.includes(userPermission.name)
            )
        );
    };

    return {
        authenticated: !!authState.accessToken,
        user: authState.user,
        accessToken: authState.accessToken,
        idToken: authState.idToken,
        resetLogin,
        login,
        relogin,
        logout,
        error: authState.error,
        hasPermission,
    };
};

const niamCodeChallenge = async (
    authorizeUrl: string,
    clientId: string,
    redirectUrl: string,
    service: string,
    scope: string,
    state?: string
) => {
    const { verifier, challenge } = await generatePKCE();
    sessionStorage.setItem("codeVerifier", verifier);
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("expiresIn");
    sessionStorage.removeItem("refreshToken");
    const params = new URLSearchParams({
        client_id: clientId,
        response_type: "code",
        scope,
        redirect_uri: redirectUrl,
        service,
        code_challenge_method: "S256",
        code_challenge: challenge,
    });
    if (state) params.set("state", state);
    window.location.href = `${authorizeUrl}?${params.toString()}`;
};

const searchWithoutAuthCallbackParams = (search: string): string => {
    const searchParams = new URLSearchParams(search);
    AUTH_CALLBACK_PARAMS.forEach((param) => searchParams.delete(param));
    return searchParams.toString();
};

const restoreLoginReturnUrl = (navigate: ReturnType<typeof useNavigate>) => {
    const returnUrl = sessionStorage.getItem(LOGIN_RETURN_URL_SESSION_KEY);
    sessionStorage.removeItem(LOGIN_RETURN_URL_SESSION_KEY);
    if (returnUrl) {
        navigate(returnUrl, { replace: true });
        return;
    }

    const search = searchWithoutAuthCallbackParams(window.location.search);
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}${
        window.location.hash
    }`;
    navigate(nextUrl, { replace: true });
};

const niamDeAuthorize = async (
    deAuthorizeUrl: string,
    idToken: string,
    accessToken: string
) => {
    try {
        const response = await fetch(
            `${deAuthorizeUrl}?id_token_hint=${idToken}`,
            { headers: getAuthHeader(accessToken) }
        );
        if (response.status === 204) {
            console.log("successfully logged out");
        }
    } catch (e) {
        console.error(e);
    }
};

const getAuthHeader = (accessToken: string | null): { [key: string]: string } =>
    accessToken
        ? {
              Authorization: `Bearer ${accessToken}`,
          }
        : {};

const useQueryParams = () => {
    const [searchParams] = useSearchParams();
    return {
        code: searchParams.get("code"),
        iss: searchParams.get("iss"),
        clientId: searchParams.get("client_id"),
    };
};

export const isTokenExpired = (token: string): boolean => {
    try {
        const { exp } = jose.decodeJwt(token);
        if (!exp) return true;
        const expirationDatetimeInSeconds = exp * 1000;
        return Date.now() >= expirationDatetimeInSeconds;
    } catch {
        return true;
    }
};

export const millisecondsUntilRefresh = (
    accessToken: string,
    minutesBeforeExpiration: number
): number | null => {
    try {
        const { exp, iat } = jose.decodeJwt(accessToken);
        if (!exp) return null;
        const secondsBeforeExpiration = iat
            ? Math.min(minutesBeforeExpiration * 60, (exp - iat) / 2)
            : minutesBeforeExpiration * 60;
        return Math.max(0, (exp - secondsBeforeExpiration) * 1000 - Date.now());
    } catch {
        return null;
    }
};

export const clearSessionAndLogout = () => {
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("codeVerifier");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("expiresIn");
    window.location.reload();
};
